// React
import React from "react";

// Components
import { Box, Breadcrumbs, Link, Skeleton, Typography } from "@mui/material";

import { useTranslation } from "react-i18next";
import { FiChevronRight } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import useCompanyLite from "hooks/companies/useCompanyLite.jsx";
// Hooks
import useService from "hooks/services/useService.jsx";
import useSubtask from "hooks/subtasks/useSubtask.jsx";
import useTask from "hooks/tasks/useTask.jsx";

// Utils
import { colors } from "styles/tokens";

/**
 * Breadcrumb component for MainNavbar
 * Shows breadcrumbs based on current route
 */
const MainBreadcrumbs = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { company_id, id } = useParams();
  const navigate = useNavigate();

  // Get company data
  const { liteSelectedCompany, isLoading: companyLoading } = useCompanyLite(
    company_id,
    Boolean(company_id)
  );

  // Determine current route type
  const isServiceRoute = location.pathname.includes("/service/");
  const isTaskRoute = location.pathname.includes("/task/");
  const isSubtaskRoute = location.pathname.includes("/subtask/");

  // Get entity data based on route
  const { service, isLoading: serviceLoading } = useService(id, isServiceRoute);
  const { task, isLoading: taskLoading } = useTask(id, isTaskRoute);
  const { subtask, isLoading: subtaskLoading } = useSubtask(id, isSubtaskRoute);

  // Don't show breadcrumbs if we're not on a relevant route
  if (!company_id || (!isServiceRoute && !isTaskRoute && !isSubtaskRoute)) {
    return null;
  }

  // Loading skeleton
  if (companyLoading || serviceLoading || taskLoading || subtaskLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="text" width={100} height={20} />
        <FiChevronRight size={12} />
        <Skeleton variant="text" width={80} height={20} />
      </Box>
    );
  }

  const breadcrumbItems = [];

  // Always add company as first breadcrumb
  if (liteSelectedCompany) {
    breadcrumbItems.push({
      label: liteSelectedCompany.name,
      secondaryLabel: `${liteSelectedCompany.client_first_name} ${liteSelectedCompany.client_last_name}`,
      href: `/${company_id}/board`,
      onClick: () => navigate(`/${company_id}/board`),
    });
  }

  // Service route breadcrumbs
  if (isServiceRoute && service) {
    // Add service type if available
    if (service.service_type) {
      breadcrumbItems.push({
        label: service.service_type.name,
        href: null,
        onClick: null,
        isText: true,
      });
    }

    // Add sub service type if available
    if (service.sub_service_type) {
      breadcrumbItems.push({
        label: service.sub_service_type.name,
        href: null,
        onClick: null,
        isText: true,
      });
    }

    // Add current service
    breadcrumbItems.push({
      label: service.name,
      secondaryLabel: `${service.client_first_name} ${service.client_last_name}`,
      href: null,
      onClick: null,
      isCurrent: true,
    });
  }

  // Task route breadcrumbs
  if (isTaskRoute && task) {
    // Add service if available
    if (task.service) {
      breadcrumbItems.push({
        label: task.service.name,
        href: `/${company_id}/service/${task.service.id}`,
        onClick: () => navigate(`/${company_id}/service/${task.service.id}`),
      });
    }

    // Add current task
    breadcrumbItems.push({
      label: task.name,
      secondaryLabel: `${task.client_first_name} ${task.client_last_name}`,
      href: null,
      onClick: null,
      isCurrent: true,
    });
  }

  // Subtask route breadcrumbs
  if (isSubtaskRoute && subtask) {
    // Add service if available
    if (subtask.task?.service) {
      breadcrumbItems.push({
        label: subtask.task.service.name,
        href: `/${company_id}/service/${subtask.task.service.id}`,
        onClick: () => navigate(`/${company_id}/service/${subtask.task.service.id}`),
      });
    }

    // Add task if available
    if (subtask.task) {
      breadcrumbItems.push({
        label: subtask.task.name,
        href: `/${company_id}/task/${subtask.task.id}`,
        onClick: () => navigate(`/${company_id}/task/${subtask.task.id}`),
      });
    }

    // Add current subtask
    breadcrumbItems.push({
      label: subtask.name,
      href: null,
      onClick: null,
      isCurrent: true,
    });
  }

  // If no breadcrumb items, don't render
  if (breadcrumbItems.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs
      separator={<FiChevronRight size={12} color={colors.gray400} />}
      aria-label={t("navigation:breadcrumbs.ariaLabel")}
      sx={{
        marginLeft: "10px",
        "& .MuiBreadcrumbs-ol": {
          alignItems: "center",
        },
      }}
    >
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;

        if (item.isText || item.isCurrent || isLast) {
          return (
            <Typography
              key={index}
              variant="body2"
              sx={{
                color: item.isCurrent ? colors.gray900 : colors.gray500,
                fontWeight: item.isCurrent ? 500 : 400,
                fontSize: "13px",
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={index}
            onClick={(e) => {
              e.preventDefault();
              if (item.onClick) {
                item.onClick();
              }
            }}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              transform: item.secondaryLabel ? "translateY(7px)" : "translateY(0px)",
              flexDirection: "column",
              color: colors.gray500,
              textDecoration: "none",
              fontSize: "13px",
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
              "&:hover": {
                color: colors.primaryStrong,
                textDecoration: "underline",
              },
            }}
          >
            {item.label}
            {item.secondaryLabel && (
              <Typography variant="body2" sx={{ color: colors.gray500, fontSize: "11px" }}>
                {item.secondaryLabel}
              </Typography>
            )}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default MainBreadcrumbs;
